import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Flame, Loader2, CheckCircle2, XCircle, CreditCard } from "lucide-react";
import { supabase } from "@/lib/supabase";

export default function AuthCallbackPage() {
  const [location, setLocation] = useLocation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'checkout'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (!supabase) {
      setStatus('error');
      setErrorMessage('Authentication not configured');
      return;
    }

    let mounted = true;

    const handleCallback = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        if (error) {
          console.error('Auth callback error:', error);
          setStatus('error');
          setErrorMessage(error.message || 'Authentication failed');
          return;
        }

        if (data?.session) {
          // Mark OAuth logins as "Remember Me" since user can't choose during OAuth flow
          sessionStorage.setItem('myFitBeats_rememberMe', 'true');
          
          // Use the session token we already have from getSession()
          const token = data.session.access_token;
          
          // Check if user needs to complete checkout (signup flow)
          try {
            const response = await fetch('/api/signup/status', {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
              const signupStatus = await response.json();
              
              // If user needs to complete checkout, redirect to Stripe
              if (signupStatus.needsCheckout) {
                setStatus('checkout');
                
                // Create checkout session (promo codes can be entered directly in Stripe checkout)
                const checkoutResponse = await fetch('/api/signup/checkout', {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({})
                });
                
                if (checkoutResponse.ok) {
                  const { url } = await checkoutResponse.json();
                  // Redirect to Stripe checkout
                  window.location.href = url;
                  return;
                } else {
                  const errorData = await checkoutResponse.json();
                  // If user already has access (beta, subscriber), just redirect home
                  if (errorData.redirect) {
                    setStatus('success');
                  } else {
                    throw new Error(errorData.error || 'Failed to start checkout');
                  }
                }
              } else {
                setStatus('success');
              }
            } else {
              // If we can't check status, proceed to success (legacy flow)
              setStatus('success');
            }
          } catch (checkoutError: any) {
            console.error('Checkout check error:', checkoutError);
            // Don't block login if checkout check fails - proceed with success
            setStatus('success');
          }
        } else {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const errorDescription = hashParams.get('error_description');
          
          if (errorDescription) {
            setStatus('error');
            setErrorMessage(decodeURIComponent(errorDescription));
          } else {
            setStatus('error');
            setErrorMessage('No session found. Please try signing in again.');
          }
        }
      } catch (err: any) {
        if (!mounted) return;
        console.error('Callback processing error:', err);
        setStatus('error');
        setErrorMessage(err.message || 'An unexpected error occurred');
      }
    };

    handleCallback();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => {
        // Full page reload to ensure auth state is picked up by Router
        window.location.href = '/';
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const handleReturnToSignIn = () => {
    setLocation('/auth');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="p-3 bg-primary/20 rounded-xl">
            <Flame className="w-8 h-8 text-primary animate-pulse" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            myFit<span className="text-primary">Beats</span>
          </h1>
        </div>

        <div className="bg-card border border-border/50 rounded-xl p-8">
          {status === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 mx-auto mb-4 text-primary animate-spin" />
              <h2 className="text-xl font-semibold mb-2">Signing you in...</h2>
              <p className="text-muted-foreground">Please wait while we complete the authentication.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
              <h2 className="text-xl font-semibold mb-2">Welcome!</h2>
              <p className="text-muted-foreground">You've been signed in successfully. Redirecting...</p>
            </>
          )}

          {status === 'checkout' && (
            <>
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-primary animate-pulse" />
              <h2 className="text-xl font-semibold mb-2">Almost there!</h2>
              <p className="text-muted-foreground">Redirecting you to add payment details...</p>
              <p className="text-xs text-muted-foreground mt-2">You won't be charged today - your 14-day free trial starts now!</p>
              <p className="text-xs text-muted-foreground">After trial: $8.99/month. Cancel anytime.</p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
              <h2 className="text-xl font-semibold mb-2">Sign in failed</h2>
              <p className="text-muted-foreground mb-4">{errorMessage}</p>
              <button
                onClick={handleReturnToSignIn}
                className="text-primary hover:underline"
              >
                Return to sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
